import { parsePeriod } from "@onestore/api/index"
import type { PriceMolProps } from "@onestore/hel/dist/components/molecules/PriceMol"
import _t from "@core/i18n"
import { CURRENCY_SEPARATOR } from "@core/pricing"
import type { PriceType } from "@core/types"
import {
  getSecondaryPrices,
  getStaticLowestPrice,
  getStaticPrices,
  getStaticRegularPrice,
} from "@gatsby-plugin-generic-page/helpers/staticPrices"
import type { PriceData } from "@ionos-web-design-system/react"
import type { PriceInfo } from "~/fragments/priceInfo"
import type { StaticPrice } from "~/fragments/staticPrice"
import isEmpty from "~/lib/isEmpty"
import { getAdditionalPriceInformation } from "../../DetailedPrice/helpers/getDetailedPriceData"

type BundleData = {
  hasPromotion: boolean
  priceInfo?: PriceInfo
  hidePeriodText?: boolean
}

type DetailedStaticPriceDataHookResults = {
  data: PriceData
  hasPromotion: boolean
} | null

export const getDetailedStaticPriceData = (
  staticPrices: StaticPrice[],
  period: string,
  currentPriceType: PriceType,
  customRenewAdditionalInfo?: string,
  bundleData?: BundleData,
  displaySuffixInAdditionalInfo?: boolean,
  dataTestId?: PriceMolProps["mainPrice"]["dataTest"]
): DetailedStaticPriceDataHookResults => {
  const prices = staticPrices
    ? getStaticPrices(staticPrices, currentPriceType)
    : {}

  if (isEmpty(prices) || isEmpty(prices[period])) {
    return null
  }

  const crossedPrices = staticPrices
    ? getSecondaryPrices(staticPrices, currentPriceType)
    : {}
  const regularPrices = staticPrices
    ? getStaticRegularPrice(staticPrices, currentPriceType)
    : {}
  const lowestPrices = staticPrices
    ? getStaticLowestPrice(staticPrices, currentPriceType)
    : {}

  const bundlePriceInfo = !isEmpty(bundleData)
    ? bundleData.priceInfo
    : undefined

  const staticAdditionalInformation = !isEmpty(prices[period]?.renewPrice)
    ? `${_t("prices.renewalPrefix")} ${prices[period].renewPrice?.value} ${prices[period].renewPrice?.suffix}`
    : customRenewAdditionalInfo

  const { periodType, periodValue } = parsePeriod(period)

  const bundleAdditionalPriceInformation = !isEmpty(bundlePriceInfo)
    ? getAdditionalPriceInformation(
        bundlePriceInfo,
        currentPriceType,
        prices[period]?.renewPrice?.value,
        periodType,
        periodValue
      )
    : []

  const hasPromotion: boolean = !isEmpty(bundleData)
    ? bundleData.hasPromotion
    : !isEmpty(crossedPrices) &&
      crossedPrices[period] &&
      !isEmpty(regularPrices) &&
      !isEmpty(regularPrices[period]) &&
      !isEmpty(lowestPrices) &&
      !isEmpty(lowestPrices[period])

  const bundlePeriodText =
    !isEmpty(bundleData) && bundleData.hidePeriodText
      ? _t(`prices.${currentPriceType}`)
      : null

  const pricePrelines = !isEmpty(prices[period].price.prefix)
    ? {
        primary: {
          preline: prices[period].price.prefix,
        },
      }
    : undefined

  const data: PriceData = {
    prelines: pricePrelines,
    main: {
      comma: CURRENCY_SEPARATOR,
      currencySymbolPosition: "after",
      decimalPrice:
        prices[period].price.value.split(CURRENCY_SEPARATOR)[1] || "00",
      integerPrice: prices[period].price.value.split(CURRENCY_SEPARATOR)[0],
      additionalInfo: displaySuffixInAdditionalInfo
        ? bundlePeriodText || prices[period].price.suffix
        : undefined,
      testId: dataTestId?.currentPriceDataTestId,
    },
    postlines: [
      ...(!displaySuffixInAdditionalInfo
        ? [
            {
              content: bundlePeriodText || prices[period].price.suffix,
              weight: "black" as const,
            },
          ]
        : []),
      ...(!isEmpty(bundleAdditionalPriceInformation)
        ? bundleAdditionalPriceInformation
        : staticAdditionalInformation
          ? [
              {
                content: staticAdditionalInformation,
                weight: "black" as const,
              },
            ]
          : []),
      {
        content:
          hasPromotion && !isEmpty(lowestPrices)
            ? _t("prices.lowestPriceWithType", {
                priceType: _t(`prices.${currentPriceType}`),
              }) +
              " " +
              lowestPrices[period].price
            : "",
        weight: "black" as const,
      },
    ].filter((item) => item.content !== ""),
  }

  return { data, hasPromotion }
}
